import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DIRECT_UPLOAD_URL = "/rails/active_storage/direct_uploads";

/** Mirrors ImageAttachable::ALLOWED_CONTENT_TYPES — the server is the authority, this is the filter
 *  that keeps a doomed upload off the wire. */
export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

/** Mirrors Messaging::Contracts::SendMessageContract::MAX_ATTACHMENTS. */
export const MAX_ATTACHMENTS = 6;

export interface UploadFile {
  id: string;
  file: File;
  previewUrl: string;
  progress: number;
  signedId: string | null;
  status: "uploading" | "completed" | "failed";
  error: string | null;
}

export interface UseDirectUploadMultipleResult {
  files: UploadFile[];
  addFiles: (files: FileList | File[]) => void;
  removeFile: (id: string) => void;
  completedSignedIds: string[];
  isUploading: boolean;
  allCompleted: boolean;
  reset: () => void;
}

export function useDirectUploadMultiple(maxFiles = Number.POSITIVE_INFINITY): UseDirectUploadMultipleResult {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const nextIdRef = useRef(0);
  const abortControllers = useRef(new Map<string, AbortController>());

  const uploadFile = useCallback(async (uploadFile: UploadFile) => {
    const { DirectUpload } = await import("@rails/activestorage");
    const abortController = new AbortController();
    abortControllers.current.set(uploadFile.id, abortController);

    const directUpload = new DirectUpload(uploadFile.file, DIRECT_UPLOAD_URL, {
      directUploadWillStoreFileWithXHR(request: XMLHttpRequest) {
        abortController.signal.addEventListener("abort", () => request.abort());

        request.upload.addEventListener("progress", (event: ProgressEvent) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setFiles((prev) => prev.map((f) => (f.id === uploadFile.id ? { ...f, progress } : f)));
          }
        });
      },
    });

    directUpload.create((error: Error, blob: { signed_id: string }) => {
      abortControllers.current.delete(uploadFile.id);

      if (error) {
        setFiles((prev) =>
          prev.map((f) => (f.id === uploadFile.id ? { ...f, status: "failed" as const, error: error.message } : f)),
        );
      } else {
        setFiles((prev) =>
          prev.map((f) =>
            f.id === uploadFile.id
              ? { ...f, status: "completed" as const, progress: 100, signedId: blob.signed_id }
              : f,
          ),
        );
      }
    });
  }, []);

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      // Capped here rather than at pickers: a single multi-select can exceed
      // maxFiles in one change event. Overflow is dropped, not errored.
      const accepted = Array.from(newFiles).slice(0, Math.max(0, maxFiles - files.length));
      const uploadFiles: UploadFile[] = accepted.map((file) => ({
        id: `upload-${++nextIdRef.current}-${Date.now()}`,
        file,
        previewUrl: URL.createObjectURL(file),
        progress: 0,
        signedId: null,
        status: "uploading" as const,
        error: null,
      }));

      setFiles((prev) => [...prev, ...uploadFiles]);

      for (const uf of uploadFiles) {
        void uploadFile(uf);
      }
    },
    [files.length, maxFiles, uploadFile],
  );

  const removeFile = useCallback((id: string) => {
    const controller = abortControllers.current.get(id);
    if (controller) {
      controller.abort();
      abortControllers.current.delete(id);
    }

    setFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file) URL.revokeObjectURL(file.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const reset = useCallback(() => {
    for (const [, controller] of abortControllers.current) {
      controller.abort();
    }
    abortControllers.current.clear();

    setFiles((prev) => {
      for (const file of prev) URL.revokeObjectURL(file.previewUrl);
      return [];
    });
  }, []);

  // Cleanup on unmount: abort in-flight uploads and revoke object URLs
  useEffect(() => reset, [reset]);

  const completedSignedIds = useMemo(
    () =>
      files
        .filter((f): f is UploadFile & { signedId: string } => f.status === "completed" && f.signedId !== null)
        .map((f) => f.signedId),
    [files],
  );
  const isUploading = useMemo(() => files.some((f) => f.status === "uploading"), [files]);
  const allCompleted = useMemo(
    () => files.length > 0 && files.every((f) => f.status === "completed" || f.status === "failed"),
    [files],
  );

  return { files, addFiles, removeFile, completedSignedIds, isUploading, allCompleted, reset };
}
